import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, Loader2, Hash, AlertTriangle, CheckCircle2, Sparkles, TrendingUp, Instagram, Zap } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { proxyImage } from "@/lib/img";
import BrandDnaCard from "@/components/app/BrandDnaCard";
import { useBrandDnaSummary } from "@/lib/useBrandDna";
import { runToolJob } from "@/lib/toolJobs";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function PriorityBadge({ priority }) {
  const map = {
    hoch: "bg-red-500/15 text-red-400 border-red-500/30",
    mittel: "bg-amber-500/15 text-amber-400 border-amber-500/30",
    niedrig: "bg-zinc-500/15 text-zinc-400 border-zinc-500/30",
  };
  return <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-md border ${map[priority] || map.mittel}`}>{priority}</span>;
}

export default function HashtagCheckPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialPlatform = searchParams.get("platform") === "tiktok" ? "tiktok" : "instagram";
  const [platform, setPlatform] = useState(initialPlatform);
  const [tag, setTag] = useState("");
  const [loading, setLoading] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [result, setResult] = useState(null);
  const { summary: dna } = useBrandDnaSummary();

  useEffect(() => {
    const p = searchParams.get("platform");
    if (p === "tiktok" || p === "instagram") setPlatform(p);
  }, [searchParams]);

  // Re-open from history via ?jobId=
  useEffect(() => {
    const jobId = searchParams.get("jobId");
    if (!jobId) return;
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        // Poll until terminal state
        const startTs = Date.now();
        const maxMs = 9 * 60 * 1000;
        while (!cancelled && Date.now() - startTs < maxMs) {
          const { data: job } = await axios.get(`${API}/tools/jobs/${jobId}`);
          if (cancelled) return;
          if (job.input?.platform) setPlatform(job.input.platform);
          if (job.input?.hashtag) setTag(job.input.hashtag);
          if (job.status === "completed" && job.result) {
            setResult(job.result);
            return;
          }
          if (job.status === "failed") {
            toast.error(job.error || "Hashtag-Check fehlgeschlagen");
            return;
          }
          setElapsed(Math.floor((Date.now() - startTs) / 1000));
          await new Promise(r => setTimeout(r, 4000));
        }
      } catch (e) {
        if (!cancelled) toast.error("Konnte gespeicherte Analyse nicht laden");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line
  }, []);

  // Pre-fill hashtag from DNA niche (first meaningful word)
  useEffect(() => {
    if (dna?.niche && !tag) {
      const tok = (dna.niche || "").split(/[\s,.]+/).map(w => w.toLowerCase().replace(/[^a-z0-9]/g, ""))
        .filter(w => w.length >= 4).slice(0, 1)[0];
      if (tok) setTag(tok);
    }
    // eslint-disable-next-line
  }, [dna]);

  const run = async () => {
    if (!tag.trim()) return;
    setLoading(true);
    setResult(null);
    setElapsed(0);
    try {
      const data = await runToolJob("post", "/analyse/hashtag-check",
        { hashtag: tag.trim(), platform },
        { etaSec: 240, onTick: ({ elapsedSec }) => setElapsed(elapsedSec) },
      );
      setResult(data);
      toast.success("Hashtag-Analyse fertig!");
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || "Hashtag-Check fehlgeschlagen");
    } finally { setLoading(false); }
  };

  const ai = result?.ai_analysis || {};
  const ht = result?.hashtag_data || {};

  const tierColor = ai.size_tier?.includes("mega") ? "text-cyan-400 bg-cyan-500/10 border-cyan-500/30"
    : ai.size_tier?.includes("groß") || ai.size_tier?.includes("gross") ? "text-amber-400 bg-amber-500/10 border-amber-500/30"
    : ai.size_tier?.includes("mittel") ? "text-cyan-400 bg-cyan-500/10 border-cyan-500/30"
    : "text-emerald-400 bg-emerald-500/10 border-emerald-500/30";

  return (
    <div className="min-h-screen bg-[#09090b]">
      <div className="max-w-3xl mx-auto px-4 py-8">
        <div className="flex items-center gap-3 mb-8">
          <Button variant="ghost" size="icon" onClick={() => navigate("/dashboard")} className="h-8 w-8 text-zinc-400 hover:text-white" data-testid="back-btn">
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <Hash className="w-5 h-5 text-cyan-400" />
          <h1 className="text-xl font-bold text-white" style={{ fontFamily: 'Outfit' }}>Hashtag-Check</h1>
          <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded bg-zinc-800 text-zinc-300 border border-zinc-700">{platform}</span>
        </div>

        {!result && (
          <div className="space-y-4">
            {dna && <BrandDnaCard summary={dna} variant="hint" />}
            {/* Platform Switch */}
            <div className="flex gap-2" data-testid="platform-switch">
              {[
                { id: "instagram", label: "Instagram", icon: Instagram },
                { id: "tiktok", label: "TikTok", icon: Zap },
              ].map(p => (
                <button
                  key={p.id}
                  onClick={() => setPlatform(p.id)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-xl border text-xs font-medium transition-all ${
                    platform === p.id ? "border-cyan-500 bg-cyan-500/10 text-cyan-400" : "border-zinc-800 text-zinc-400 hover:border-zinc-700"
                  }`}
                  data-testid={`platform-${p.id}`}
                >
                  <p.icon className="w-3.5 h-3.5" />
                  {p.label}
                </button>
              ))}
            </div>
            <p className="text-sm text-zinc-400">Gib einen Hashtag ein und du bekommst die Top-Posts, Volumen, Konkurrenz-Level und konkrete Tipps wie du den Tag strategisch nutzt.</p>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500">#</span>
              <Input
                value={tag}
                onChange={e => setTag(e.target.value.replace(/^#/, ""))}
                onKeyDown={e => e.key === "Enter" && run()}
                placeholder="z.B. mindset, businesscoach, reelstipps..."
                className="bg-zinc-900 border-zinc-800 text-white h-11 pl-7"
                data-testid="hashtag-input"
              />
            </div>
            <Button onClick={run} disabled={loading || !tag.trim()} className="w-full bg-gradient-to-r from-cyan-500 to-lime-400 hover:from-cyan-400 hover:to-lime-300 text-white h-11" data-testid="run-hashtag-btn">
              {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Hashtag wird analysiert...</> : <><Sparkles className="w-4 h-4 mr-2" />Hashtag analysieren</>}
            </Button>
            {loading && (
              <div className="text-xs text-zinc-400 text-center space-y-1" data-testid="hashtag-progress">
                <p>{elapsed > 0 ? `~${elapsed}s gelaufen` : "Starte Analyse..."} · Apify scraped Instagram Top-Posts (typisch 2–8 Min)</p>
                {elapsed > 30 && <p className="text-zinc-500">Du kannst diesen Tab offen lassen oder später aus „Meine letzten Analysen" wieder öffnen.</p>}
              </div>
            )}
          </div>
        )}

        {result && (
          <div className="space-y-4" data-testid="hashtag-result">
            {/* Hero */}
            <div className="p-5 rounded-xl bg-gradient-to-br from-cyan-500/10 to-lime-400/5 border border-cyan-500/30">
              <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                <h2 className="text-2xl font-bold text-white" style={{ fontFamily: 'Outfit' }}>#{ht.hashtag}</h2>
                <span className={`text-xs uppercase tracking-wider px-2 py-1 rounded border ${tierColor}`}>{ai.size_tier}</span>
              </div>
              <div className="grid grid-cols-3 gap-3 mb-3">
                <div className="p-2 rounded-lg bg-zinc-900/60 border border-zinc-800">
                  <p className="text-[10px] uppercase text-zinc-500">Posts gesamt</p>
                  <p className="text-sm font-bold text-white">{ht.posts_count_total ? ht.posts_count_total.toLocaleString() : "—"}</p>
                </div>
                <div className="p-2 rounded-lg bg-zinc-900/60 border border-zinc-800">
                  <p className="text-[10px] uppercase text-zinc-500">Avg Likes</p>
                  <p className="text-sm font-bold text-white">{ht.avg_likes?.toLocaleString()}</p>
                </div>
                <div className="p-2 rounded-lg bg-zinc-900/60 border border-zinc-800">
                  <p className="text-[10px] uppercase text-zinc-500">Avg Views</p>
                  <p className="text-sm font-bold text-white">{ht.avg_views?.toLocaleString()}</p>
                </div>
              </div>
              <p className="text-sm text-zinc-200 mb-2">{ai.verdict}</p>
              <div className="flex gap-2 flex-wrap text-xs">
                {ai.competition_level && <span className="px-2 py-0.5 rounded bg-amber-500/10 text-amber-400 border border-amber-500/30">Konkurrenz: {ai.competition_level}</span>}
                {ai.best_for && <span className="px-2 py-0.5 rounded bg-cyan-500/10 text-cyan-400 border border-cyan-500/30">Best für: {ai.best_for}</span>}
              </div>
            </div>

            {ai.warning && (
              <div className="p-3 rounded-xl bg-red-500/10 border border-red-500/30 flex gap-2">
                <AlertTriangle className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-red-200/90">{ai.warning}</p>
              </div>
            )}

            {/* Content patterns */}
            {ai.content_patterns?.length > 0 && (
              <div className="p-4 rounded-xl bg-zinc-900 border border-zinc-800">
                <div className="flex items-center gap-2 mb-2">
                  <TrendingUp className="w-4 h-4 text-cyan-400" />
                  <p className="text-sm font-semibold text-white">Erkannte Content-Muster</p>
                </div>
                <ul className="space-y-1">{ai.content_patterns.map((p, i) => <li key={i} className="text-xs text-zinc-300">▸ {p}</li>)}</ul>
              </div>
            )}

            {/* What top performers do */}
            {ai.what_top_performers_do?.length > 0 && (
              <div className="p-4 rounded-xl bg-emerald-500/5 border border-emerald-500/20">
                <p className="text-sm font-semibold text-emerald-300 mb-2">Was die Top-Performer richtig machen</p>
                <ul className="space-y-1">{ai.what_top_performers_do.map((p, i) => <li key={i} className="text-xs text-emerald-200/90">✓ {p}</li>)}</ul>
              </div>
            )}

            {/* Related hashtags */}
            {ai.related_hashtags?.length > 0 && (
              <div className="p-4 rounded-xl bg-zinc-900 border border-zinc-800">
                <p className="text-sm font-semibold text-white mb-2">Verwandte Hashtags</p>
                <div className="flex flex-wrap gap-1.5">
                  {ai.related_hashtags.map((h, i) => <span key={i} className="text-xs px-2 py-0.5 rounded-md bg-cyan-500/10 text-cyan-400">#{h.replace('#','')}</span>)}
                </div>
              </div>
            )}

            {/* Strategy tips */}
            {ai.strategy_tips?.length > 0 && (
              <div className="p-4 rounded-xl bg-zinc-900 border border-zinc-800">
                <p className="text-sm font-semibold text-white mb-2">Strategische Tipps</p>
                <ol className="space-y-1.5">{ai.strategy_tips.map((p, i) => <li key={i} className="text-xs text-zinc-300 flex gap-2"><span className="text-cyan-400 font-bold">{i + 1}.</span><span>{p}</span></li>)}</ol>
              </div>
            )}

            {/* Top posts */}
            {ht.top_posts?.length > 0 && (
              <div className="p-4 rounded-xl bg-zinc-900 border border-zinc-800">
                <p className="text-sm font-semibold text-white mb-3">Top-Posts mit diesem Hashtag</p>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  {(ht.top_posts || []).slice(0, 8).map((p, i) => (
                    <a key={i} href={p.url} target="_blank" rel="noreferrer" className="group block">
                      {p.thumbnail && <img src={proxyImage(p.thumbnail)} alt="" className="w-full aspect-square rounded-lg object-cover border border-zinc-800 group-hover:border-cyan-500/40 transition-colors" />}
                      <p className="text-[10px] text-zinc-500 mt-1 truncate">@{p.owner_username}</p>
                      <p className="text-[10px] text-zinc-400">❤️ {p.likes?.toLocaleString()}</p>
                    </a>
                  ))}
                </div>
              </div>
            )}

            {/* Checklist */}
            {ai.checklist?.length > 0 && (
              <div className="p-4 rounded-xl bg-zinc-900 border border-zinc-800">
                <div className="flex items-center gap-2 mb-3">
                  <CheckCircle2 className="w-4 h-4 text-cyan-400" />
                  <p className="text-sm font-semibold text-white">Deine Hashtag-Checkliste</p>
                </div>
                <ul className="space-y-2">
                  {ai.checklist.map((c, i) => (
                    <li key={i} className="p-3 rounded-lg bg-zinc-950 border border-zinc-800">
                      <div className="flex items-center gap-2 mb-1"><PriorityBadge priority={c.priority} /></div>
                      <p className="text-xs text-zinc-200">{c.task}</p>
                      {c.impact && <p className="text-[10px] text-zinc-500 mt-0.5">→ {c.impact}</p>}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <Button onClick={() => { setResult(null); setTag(""); }} variant="outline" className="w-full border-zinc-700 text-zinc-400">Anderen Hashtag analysieren</Button>
          </div>
        )}
      </div>
    </div>
  );
}
